"use strict";
/**
 * Canonical usage-visibility vocabulary for material Usage_Partitions (GSI3).
 *
 * A Link_Row (`PK=entity#{entityId}`, `SK=material#{materialId}`) records that a pattern or project
 * uses a material. Its `GSI3PK` places it in a **Usage_Partition**:
 *
 *     {pattern|project}_material#{materialId}#{public|private|blocked}
 *
 * `usagePartitionKey` below is the ONLY sanctioned way to build that key. Six partitions exist —
 * two entity types x three visibilities — and a reader that enumerates a subset silently answers
 * "this material is not in use" for usage that is right there.
 *
 * ## THIS IS NOT THE `Visibility` ENUM. Do not substitute it.
 *
 * The two vocabularies overlap on two of three members, which is exactly why they get conflated:
 *
 *     Visibility       (./visibility.ts)  public | unlisted | private   <- Access_Level, owner choice
 *     UsageVisibility  (this file)        public | private  | blocked   <- Usage_Partition suffix
 *
 * Substituting `Visibility` for a usage suffix breaks the set in BOTH directions at once:
 *
 * - It **adds `#unlisted`, a partition nothing ever writes.** An unlisted entity's Link_Rows are
 *   stamped `#private`, because `unlisted` is a link-sharing affordance and a Usage_Partition is not
 *   a link — see `VISIBILITY_TO_USAGE_VISIBILITY` below. A reader enumerating `#unlisted` therefore
 *   queries an empty partition, gets zero rows, and concludes the material is unused.
 *   That failure mode is not hypothetical: it is exactly BL-118 and BL-120, which the same mistake
 *   produced with the now-removed `#archived` (a guard permitted a delete it should have refused, and
 *   a migration could not reach the usage a guard blocked on — a material neither deletable nor
 *   migratable). `unlisted` is the new way to make it, so it is named here explicitly.
 * - It **drops `#blocked`**, which IS written. A moderation-blocked project's Link_Rows are flipped
 *   to `project_material#{materialId}#blocked`
 *   (`services/lambda-secure/src/routes/unified-flag-resolution.ts:977`). Usage parked there becomes
 *   invisible to every guard and unreachable by every migration.
 *
 * `visibilityToUsageVisibility` is the single place the two vocabularies are allowed to meet.
 *
 * ## `#archived` is gone from BOTH vocabularies — and the write path never changed
 *
 * `Visibility.ARCHIVED` was removed by spec `content-visibility-model`: archival is a Shelf_State on
 * an axis orthogonal to Access_Level, not a visibility. So the old warning about `#archived` being "a
 * partition nothing ever writes" no longer has an enum member to warn about. Nothing about the rows
 * changed: archiving a pattern still stamps `#private`
 * (`services/lambda-secure/src/routes/patterns/archive-pattern.ts` passes
 * `to: USAGE_VISIBILITY.PRIVATE` to `runMaterialVisibilityTransition`), which now follows from
 * Requirement 2.4 lowering a public item to `unlisted` on archive plus `unlisted -> private` here.
 * There was no `#archived` partition before and there is none now.
 *
 * ## The `#hidden` drift this constant existed to end — CLOSED by task 6, 2026-08-26
 *
 * `packages/lambda-utilities/src/services/material-linking.ts` used to invent a FOURTH, non-existent
 * suffix: `repointMaterialUsage` enumerated `['public', 'hidden']` and three write paths derived a
 * suffix as `record.GSI3PK?.includes('#public') ? 'public' : 'hidden'`. Every one of those literals is
 * gone as of task 6; the enumerations are `USAGE_VISIBILITIES` and the write sides derive their suffix
 * from `usageVisibilityFromPartitionKey` below. `hidden` is deliberately absent from the constant, so
 * there is nothing for it to come back from.
 *
 * **No `#hidden` row can exist in the table**, established by tracing all three former write sites
 * rather than assuming: two of them were fed by GSI3 queries on `…#public`, so their ternary could
 * only ever yield `'public'`; the third was fed by a base-table query for a record shape
 * (`PK=material#{id}`, `SK=project#{id}`) that no writer in the repo produces, so its loop body never
 * ran. Nothing therefore needs migrating out of `#hidden` — the bug was reads finding nothing, not
 * rows in the wrong place.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.6 (spec `material-usage-authority`);
 * 15.1, 15.2, 15.3, 15.4 (spec `content-visibility-model`).
 */
// Created: 2026-08-26
// Updated: 2026-09-03
Object.defineProperty(exports, "__esModule", { value: true });
exports.USAGE_ENTITY_TYPES = exports.USAGE_VISIBILITIES = exports.USAGE_VISIBILITY = void 0;
exports.usagePartitionKey = usagePartitionKey;
exports.usageVisibilityFromPartitionKey = usageVisibilityFromPartitionKey;
exports.visibilityToUsageVisibility = visibilityToUsageVisibility;
const visibility_1 = require("./visibility");
/**
 * The visibility suffix set used to build a Usage_Partition key. Exactly three members.
 *
 * Adding a fourth state is a one-line change here, and every enumeration built from
 * `USAGE_VISIBILITIES` picks it up — which is the entire reason this constant exists.
 */
exports.USAGE_VISIBILITY = {
    PUBLIC: 'public',
    PRIVATE: 'private',
    BLOCKED: 'blocked',
};
/**
 * Every usage suffix, for readers that must enumerate all partitions.
 *
 * Derived from `USAGE_VISIBILITY` rather than written out again, so the two cannot disagree.
 */
exports.USAGE_VISIBILITIES = Object.values(exports.USAGE_VISIBILITY);
/** Both entity types, for readers that must enumerate all six partitions. */
exports.USAGE_ENTITY_TYPES = ['pattern', 'project'];
/**
 * Build a Link_Row's `GSI3PK` — the only sanctioned way to construct a Usage_Partition key.
 *
 * The format is fixed by the rows already in the table and must stay byte-identical to what every
 * existing writer stamps; a divergence orphans Link_Rows into a partition nothing queries. Verified
 * against `services/lambda-secure/src/services/material-records.ts:111` and `:145`
 * (`${entityType}_material#${materialId}#${visibility}`), and against the individual writers in
 * `packages/lambda-utilities/src/services/material-linking.ts` and
 * `services/lambda-secure/src/routes/unified-flag-resolution.ts:977`.
 */
function usagePartitionKey(entityType, materialId, visibility) {
    return `${entityType}_material#${materialId}#${visibility}`;
}
/**
 * The inverse of `usagePartitionKey`: read a Link_Row's existing suffix back out of its `GSI3PK`.
 *
 * This exists because a whole family of writers RE-KEY an existing row onto a different material —
 * merge, contribution migration, the change-link repoint — and each of them has to carry the row's
 * current Usage_Partition across unchanged. Before this function they each hand-derived it, and every
 * one of them got it wrong in a different way (all found 2026-08-26, spec task 6):
 *
 * - `oldGSI3PK.endsWith('#public') ? 'public' : 'private'` — a THREE-into-TWO collapse that rewrites a
 *   `#blocked` row as `#private`, silently discarding a moderation disposition (BL-144). Its comment
 *   read "#public or #private", i.e. the author did not know `#blocked` existed.
 * - `GSI3PK?.includes('#public') ? 'public' : 'hidden'` — invents a partition no reader queries.
 * - `GSI3PK: \`pattern_material#${targetId}#public\`` — spreads the source row and then hardcodes the
 *   suffix, PUBLISHING the usage of private and moderation-blocked entities (BL-143).
 *
 * All three are the same mistake: re-deriving a value the row already carries. Returning `undefined`
 * for an unrecognised suffix is deliberate — a caller must decide what to do about a row it cannot
 * classify, rather than inheriting a silent fallback that lands it in `#public`.
 *
 * Accepts the whole `GSI3PK`, not just the suffix, because that is what a re-keying writer has in
 * hand. Tolerates `undefined` for the same reason: the field is optional on some record shapes.
 */
function usageVisibilityFromPartitionKey(gsi3pk) {
    if (!gsi3pk)
        return undefined;
    const suffix = gsi3pk.slice(gsi3pk.lastIndexOf('#') + 1);
    return exports.USAGE_VISIBILITIES.find((visibility) => visibility === suffix);
}
/**
 * The ONE mapping from an entity's Access_Level (`Visibility`) to the Usage_Partition its Link_Rows
 * belong in.
 *
 * Both non-public levels map to `PRIVATE`, which is the whole of spec `content-visibility-model`
 * Requirement 15.1: adding `unlisted` creates NO new Usage_Partition and repartitions NO Link_Row.
 * The six partitions (two entity types x three suffixes) are exactly as they were.
 *
 * `UNLISTED` -> `private` rather than `public` because the usage suffix tracks whether the usage is
 * publicly visible, and an unlisted item is out of every discovery surface. It is link-readable, but
 * a Usage_Partition is not a link — it is what a "which entities use this material" reader
 * enumerates, and that reader must not surface usage the owner has taken out of discovery.
 *
 * Declared as a total `Record<Visibility, UsageVisibility>` on purpose, and that totality is doing
 * real work in this spec: removing `ARCHIVED` and adding `UNLISTED` fails every stale site to
 * compile until someone makes a deliberate decision, rather than letting a `default` branch guess.
 *
 * Note the mapping is deliberately NOT total in the other direction. `BLOCKED` is not reachable
 * from `Visibility` at all; it is a moderation disposition, not an owner-chosen access level, which
 * is the second reason these two vocabularies must stay separate.
 *
 * There is no entry for `archived` because it is no longer an Access_Level — see the file header for
 * why the archived write path is nonetheless byte-identical to what it was.
 */
const VISIBILITY_TO_USAGE_VISIBILITY = {
    [visibility_1.Visibility.PUBLIC]: exports.USAGE_VISIBILITY.PUBLIC,
    [visibility_1.Visibility.UNLISTED]: exports.USAGE_VISIBILITY.PRIVATE,
    [visibility_1.Visibility.PRIVATE]: exports.USAGE_VISIBILITY.PRIVATE,
};
/**
 * Translate an entity Access_Level into the Usage_Partition suffix its Link_Rows carry.
 *
 * `UNLISTED` -> `private`. See `VISIBILITY_TO_USAGE_VISIBILITY` above for why.
 */
function visibilityToUsageVisibility(visibility) {
    return VISIBILITY_TO_USAGE_VISIBILITY[visibility];
}
